import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Search } from "lucide-react";

const rupiah = (v) => "Rp" + Math.round(Number(v || 0)).toLocaleString("id-ID");
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const makeKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
const blankLine = () => ({
  key: makeKey(), category: "Operasional", entity: "ingredient",
  item_id: "", item_name: "", search: "", qty: "1", unit: "pcs",
  amount: "", payment_method: "cash", notes: "",
});

function SortHead({ label, field, config, onSort, type = "text" }) {
  const active = config.key === field;
  return (
    <button type="button" onClick={() => onSort(field, type)}
      className="inline-flex items-center gap-1 font-semibold hover:text-orange-600">
      {label}<span className={active ? "text-orange-600" : "text-slate-400"}>
        {active ? (config.direction === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );
}

function compare(a, b, key, type) {
  if (type === "number") return Number(a?.[key] || 0) - Number(b?.[key] || 0);
  if (type === "date") return (new Date(a?.[key] || 0).getTime() || 0) - (new Date(b?.[key] || 0).getTime() || 0);
  return String(a?.[key] ?? "").localeCompare(String(b?.[key] ?? ""), "id", { numeric: true, sensitivity: "base" });
}

export default function ExpensesPage() {
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(today());
  const [locationType, setLocationType] = useState("outlet");
  const [locationId, setLocationId] = useState("");
  const [lines, setLines] = useState([blankLine()]);
  const [receiptNo, setReceiptNo] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc", type: "date" });

  const locations = locationType === "outlet" ? outlets : warehouses;
  const total = useMemo(() => items.reduce((sum, x) => sum + Number(x.amount || 0), 0), [items]);
  const draftTotal = useMemo(() => lines.reduce((sum, x) => sum + Number(x.amount || 0), 0), [lines]);

  const load = async () => {
    try {
      const { data } = await api.get("/expenses");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat riwayat pengeluaran");
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/outlets"),
      api.get("/warehouses"),
      api.get("/ingredients", { params: { active: true } }),
      api.get("/products"),
    ]).then(([o, w, i, p]) => {
      const outletRows = Array.isArray(o.data) ? o.data : [];
      const warehouseRows = Array.isArray(w.data) ? w.data : [];
      setOutlets(outletRows);
      setWarehouses(warehouseRows);
      setIngredients(Array.isArray(i.data) ? i.data : []);
      setProducts(Array.isArray(p.data) ? p.data.filter(x => x.active !== false) : []);
      setLocationId(outletRows[0]?.id || warehouseRows[0]?.id || "");
    }).catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat data master"));
    load();
  }, []);

  const changeLocationType = (type) => {
    setLocationType(type);
    const next = type === "outlet" ? outlets : warehouses;
    setLocationId(next[0]?.id || "");
  };

  const updateLine = (key, patch) => setLines(prev => prev.map(line => line.key === key ? { ...line, ...patch } : line));
  const addLine = () => setLines(prev => [...prev, blankLine()]);
  const removeLine = (key) => setLines(prev => prev.length > 1 ? prev.filter(x => x.key !== key) : prev);

  const selectMasterItem = (line, value) => {
    if (value === "__manual__") {
      updateLine(line.key, { item_id: "", item_name: "", search: "" });
      return;
    }
    const [entity, ...idParts] = value.split(":");
    const id = idParts.join(":");
    const master = (entity === "ingredient" ? ingredients : products).find(x => x.id === id);
    if (!master) return;
    updateLine(line.key, {
      entity, item_id: master.id, item_name: master.name || "",
      search: master.name || "", unit: master.unit || master.unit_name || line.unit,
    });
  };

  const itemOptions = (line) => {
    const source = line.entity === "ingredient" ? ingredients : products;
    const q = line.search.trim().toLowerCase();
    return source.filter(x => !q || String(x.name || "").toLowerCase().includes(q)).slice(0, 100);
  };

  async function create() {
    if (!date || !locationId || !lines.length) {
      toast.error("Tanggal, lokasi, dan minimal satu baris wajib diisi");
      return;
    }
    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      if (!line.item_name.trim()) {
        toast.error(`Nama item pada baris ${index + 1} wajib diisi`);
        return;
      }
      if (!(Number(line.amount) > 0) || !Number.isFinite(Number(line.amount))) {
        toast.error(`Nominal pada baris ${index + 1} harus lebih besar dari 0`);
        return;
      }
      if (line.category === "Persediaan" && (!line.item_id || !(Number(line.qty) > 0))) {
        toast.error(`Persediaan pada baris ${index + 1} wajib memilih item master dan kuantitas valid`);
        return;
      }
    }

    const payload = {
      date,
      outlet_id: locationType === "outlet" ? locationId : null,
      location_type: locationType,
      location_id: locationId,
      idempotency_key: makeKey(),
      lines: lines.map(line => ({
        category: line.category,
        item_id: line.category === "Persediaan" ? line.item_id : null,
        entity: line.category === "Persediaan" ? line.entity : null,
        item_name: line.item_name.trim(),
        qty: Number(line.qty || 0),
        unit: line.unit || "",
        amount: Number(line.amount),
        payment_method: line.payment_method,
        notes: line.notes.trim(),
      })),
    };

    setSaving(true);
    try {
      const { data } = await api.post("/expenses", payload);
      setReceiptNo(data?.expense?.expense_no || "");
      toast.success(`Pengeluaran tersimpan${data?.expense?.expense_no ? ` · ${data.expense.expense_no}` : ""}`);
      setDate(today());
      setLines([blankLine()]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pengeluaran");
    } finally {
      setSaving(false);
    }
  }

  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key, type, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
  }));
  const sortedItems = [...items].sort((a, b) => compare(a, b, sortConfig.key, sortConfig.type) * (sortConfig.direction === "asc" ? 1 : -1));
  const locationName = (row) => row.location_name ||
    (row.location_type === "warehouse" ? warehouses : outlets).find(x => x.id === (row.location_id || row.outlet_id))?.name ||
    outlets.find(x => x.id === row.outlet_id)?.name || "-";

  return (
    <div className="page-shell space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div data-workspace-redundant-header><div>
          <h1 className="text-2xl font-extrabold font-display">Pengeluaran</h1></div>
          <p className="text-slate-500 text-sm">Total riwayat: {rupiah(total)}</p>
          {receiptNo && <p className="text-xs text-emerald-700">Bukti terakhir: {receiptNo}</p>}
        </div>
      </div>

      <Card className="border-orange-200">
        <CardHeader><CardTitle className="text-lg">Formulir Pengeluaran</CardTitle></CardHeader>
        <CardContent className="space-y-4">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Tanggal</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)}/></div>
            <div><Label>Jenis Lokasi</Label>
              <Select value={locationType} onValueChange={changeLocationType}>
                <SelectTrigger><SelectValue placeholder="Pilih jenis lokasi"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="outlet">Outlet</SelectItem>
                  <SelectItem value="warehouse">Gudang</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{locationType === "outlet" ? "Outlet" : "Gudang"}</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger><SelectValue placeholder="Pilih lokasi"/></SelectTrigger>
                <SelectContent>{locations.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap mt-3">
            <h3 className="font-semibold">Rincian Pengeluaran</h3>
            <Button type="button" size="sm" variant="outline" onClick={addLine}><Plus className="w-4 h-4 mr-1"/>Tambah Baris</Button>
          </div>

          <div className="space-y-4">
            {lines.map((line, index) => (
              <Card key={line.key} className="border-slate-200">
                <CardHeader className="py-3 flex-row items-center justify-between">
                  <CardTitle className="text-sm">Item #{index + 1}</CardTitle>
                  <Button type="button" size="icon" variant="ghost" disabled={lines.length === 1} onClick={() => removeLine(line.key)} aria-label="Hapus baris"><Trash2 className="w-4 h-4 text-rose-500"/></Button>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div><Label>Kategori</Label>
                    <Select value={line.category} onValueChange={v => updateLine(line.key, {
                      category: v, item_id: "", item_name: "", search: "",
                      entity: "ingredient", qty: v === "Persediaan" ? "1" : "0",
                    })}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Persediaan">Persediaan</SelectItem>
                        <SelectItem value="Operasional">Operasional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {line.category === "Persediaan" && (
                    <div><Label>Jenis Master</Label>
                      <Select value={line.entity} onValueChange={v => updateLine(line.key, { entity: v, item_id: "", item_name: "", search: "" })}>
                        <SelectTrigger><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ingredient">Bahan</SelectItem>
                          <SelectItem value="product">Produk</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className={line.category === "Operasional" ? "md:col-span-2" : ""}>
                    <Label>{line.category === "Persediaan" ? "Cari item master" : "Nama pengeluaran"}</Label>
                    {line.category === "Persediaan" ? (
                      <>
                        <div className="relative">
                          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400"/>
                          <Input className="pl-9" value={line.search}
                            placeholder="Ketik nama bahan/produk..."
                            onChange={e => updateLine(line.key, { search: e.target.value, item_id: "", item_name: "" })}/>
                        </div>
                        <div className="mt-1 max-h-32 overflow-y-auto rounded border bg-white">
                          {itemOptions(line).map(x => (
                            <button type="button" key={x.id}
                              className={`block w-full text-left px-3 py-2 text-sm hover:bg-orange-50 ${line.item_id === x.id ? "bg-orange-50 font-semibold" : ""}`}
                              onClick={() => selectMasterItem(line, `${line.entity}:${x.id}`)}>
                              {x.name} <span className="text-slate-400">· {x.unit || x.unit_name || "satuan tidak diatur"}</span>
                            </button>
                          ))}
                          {!itemOptions(line).length && <p className="px-3 py-2 text-xs text-slate-400">Item tidak ditemukan di master.</p>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Terpilih: {line.item_name || "Belum dipilih"}</p>
                      </>
                    ) : (
                      <Input value={line.item_name} placeholder="Contoh: Konsumsi rapat"
                        onChange={e => updateLine(line.key, { item_name: e.target.value })}/>
                    )}
                  </div>

                  <div><Label>Nominal total (Rp)</Label>
                    <Input type="number" min="1" step="1" value={line.amount} placeholder="0"
                      onChange={e => updateLine(line.key, { amount: e.target.value })}/>
                  </div>

                  {line.category === "Persediaan" && <>
                    <div><Label>Qty</Label><Input type="number" min="0.001" step="any" value={line.qty}
                      onChange={e => updateLine(line.key, { qty: e.target.value })}/></div>
                    <div><Label>Satuan</Label><Input value={line.unit} onChange={e => updateLine(line.key, { unit: e.target.value })}/></div>
                  </>}

                  <div><Label>Metode Pembayaran</Label>
                    <Select value={line.payment_method} onValueChange={v => updateLine(line.key, { payment_method: v })}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Tunai</SelectItem>
                        <SelectItem value="transfer">Transfer</SelectItem>
                        <SelectItem value="kredit">Kredit / Utang</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2"><Label>Catatan</Label>
                    <Input value={line.notes} placeholder="Catatan opsional"
                      onChange={e => updateLine(line.key, { notes: e.target.value })}/>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="rounded-lg bg-slate-50 border p-4 flex items-center justify-between">
            <span className="font-semibold">Total pengeluaran</span>
            <span className="text-xl font-extrabold text-orange-600">{rupiah(draftTotal)}</span>
          </div>

          <div className="flex justify-end">
            <Button data-testid="expense-form-submit" disabled={saving || !locationId || !lines.length}
              onClick={create} className="bg-orange-500 hover:bg-orange-600">
              {saving ? "Menyimpan..." : "Simpan Pengeluaran"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Riwayat Pengeluaran</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead><SortHead label="Tanggal" field="date" type="date" config={sortConfig} onSort={toggleSort}/></TableHead>
                <TableHead>Bukti</TableHead>
                <TableHead><SortHead label="Lokasi" field="location_name" config={sortConfig} onSort={toggleSort}/></TableHead>
                <TableHead><SortHead label="Kategori" field="category" config={sortConfig} onSort={toggleSort}/></TableHead>
                <TableHead><SortHead label="Petugas" field="user_name" config={sortConfig} onSort={toggleSort}/></TableHead>
                <TableHead>Detail</TableHead>
                <TableHead><SortHead label="Nominal" field="amount" type="number" config={sortConfig} onSort={toggleSort}/></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sortedItems.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs whitespace-nowrap">{e.date || (e.created_at || "").slice(0, 10) || "-"}</TableCell>
                    <TableCell className="text-xs font-mono">{e.expense_no || "-"}</TableCell>
                    <TableCell>{locationName(e)}</TableCell>
                    <TableCell><Badge variant="outline">{e.category || "-"}</Badge></TableCell>
                    <TableCell className="text-xs">{e.user_name || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500 min-w-48">
                      {Array.isArray(e.lines) && e.lines.length
                        ? e.lines.map((line, i) => <div key={i}>{line.item_name} · {line.payment_method} · {rupiah(line.amount)}</div>)
                        : (e.notes || "-")}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-rose-600 whitespace-nowrap">{rupiah(e.amount)}</TableCell>
                  </TableRow>
                ))}
                {!sortedItems.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada riwayat pengeluaran</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
